import React, { useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { DatePicker, Select, Avatar, Space } from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

export interface ExportPayload {
  startDate: Dayjs;
  endDate: Dayjs;
  stage: string;
  employeeUID?: number;
}

interface ExportTodoModalProps {
  show: boolean;
  onClose: () => void;
  companyName?: string;
  employeeOptions: any[];
  /** Performs validation + the actual export request. Awaited for the spinner. */
  onExport: (payload: ExportPayload) => Promise<void>;
}

/**
 * Date-range / stage / employee export dialog (List view). Owns its own form
 * state; the parent supplies the employee list and the export implementation.
 */
const ExportTodoModal: React.FC<ExportTodoModalProps> = ({
  show,
  onClose,
  companyName,
  employeeOptions,
  onExport,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(1, "month"),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [stage, setStage] = useState<string>("all");
  const [employeeUID, setEmployeeUID] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setStartDate(dayjs().subtract(1, "month"));
    setEndDate(dayjs());
    setStage("all");
    setEmployeeUID(undefined);
    onClose();
  };

  const handleExport = async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      await onExport({ startDate, endDate, stage, employeeUID });
    } finally {
      setLoading(false);
      setEmployeeUID(undefined);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Export Todo List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  format="DD MMM YYYY"
                  placeholder="Select start date"
                  className="w-100"
                  style={{ height: "38px" }}
                  allowClear
                />
                {!startDate && (
                  <div className="text-danger small mt-1">
                    Start date is required
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  format="DD MMM YYYY"
                  placeholder="Select end date"
                  className="w-100"
                  style={{ height: "38px" }}
                  allowClear
                  disabledDate={(current) => {
                    if (!startDate || !current) return false;
                    return current.isBefore(startDate, "day");
                  }}
                />
                {!endDate && (
                  <div className="text-danger small mt-1">
                    End date is required
                  </div>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Stages</Form.Label>
                <Form.Select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                >
                  <option value="all">All Stages</option>
                  <option value="pending">Pending</option>
                  <option value="inprogress">In Progress</option>
                  <option value="onhold">On-hold</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Employee Filter</Form.Label>
                <Select
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="Select Employee"
                  allowClear
                  showSearch
                  value={employeeUID}
                  onChange={(_value, option: any) =>
                    setEmployeeUID(option?.uid)
                  }
                  dropdownStyle={{ zIndex: 9999 }}
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={employeeOptions}
                  optionRender={(option) => (
                    <Space>
                      {option?.data?.image_url && (
                        <Avatar
                          size="small"
                          src={option?.data?.image_url || undefined}
                          icon={<UserOutlined />}
                        />
                      )}
                      <span>{option.label}</span>
                    </Space>
                  )}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Form.Group className="mb-3">
                <Form.Label>Applied Filters</Form.Label>
                <div className="border rounded p-3 bg-light">
                  <div className="mb-2">
                    <strong>Company:</strong> {companyName || "All Companies"}
                  </div>
                  <div>
                    <strong>Date Range:</strong>{" "}
                    {startDate?.format("DD/MM/YYYY")} to{" "}
                    {endDate?.format("DD/MM/YYYY")}
                  </div>
                </div>
              </Form.Group>
            </div>
          </div>

          <div className="alert alert-info mb-0">
            <i className="ti ti-info-circle me-2"></i>
            This will export all todo records matching the selected filters.
            Large date ranges may take longer to process.
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Exporting...
            </>
          ) : (
            <>
              <i className="ti ti-download me-1"></i> Export Todos
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportTodoModal;
